import React, {useState} from 'react';
import _ from "lodash"
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { Field, Form, Formik, FieldArray } from 'formik';
import { useDispatch } from 'react-redux';
import { TextField, Select as SelectInput } from 'formik-mui';
import {FormControl, Grid, Link, MenuItem, IconButton} from "@mui/material";
import Button from "@mui/material/Button";
import { Box } from "@mui/system";
import Typography from "@mui/material/Typography";
import { styled } from '@mui/material/styles';
import Logo from "images/TPP_Logo_Rev.svg";
import CircularProgress from "@mui/material/CircularProgress";
import UploadIcon from "@mui/icons-material/Upload";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {submitAnonymousKYC} from "redux/kyc/kycSlice";
import Collapse from "@mui/material/Collapse";
import ReCAPTCHA from "react-google-recaptcha";



const ProfileBoxWrapper = styled(Box)(
  ({theme}) => ({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: '100vh',
      backgroundColor: '#000',
    }
  ),
)

const ProfileBox = styled(Box)(
  ({theme}) => ({
    maxWidth: '650px',
    padding: '50px',
    backgroundColor: theme.main.bgDark,
    color: theme.palette.light.main,
  }),
)

const StyledTextField = styled(TextField)(
  ({theme}) => ({
    '& .MuiFormLabel-root': {
      backgroundColor: theme.main.bgLight,
      paddingLeft: '10px',
      paddingRight: '10px',
      borderRadius: '3px'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.main.bgLight,
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.light.main,
    },
    '& label.Mui-focused': {
      color: 'gray',
    },

  }),
)

const StyledFormControl = styled(FormControl)(
  ({theme}) => ({
    '& .MuiFormLabel-root': {
      backgroundColor: theme.main.bgLight,
      paddingLeft: '10px',
      paddingRight: '10px',
      borderRadius: '3px'
    },
    '& .MuiFormControlLabel-label.Mui-disabled': {
      color: `${theme.palette.light.main} !important`
    },
    '& .MuiFormHelperText-root': {
      fontSize: '1.4rem',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.main.bgLight,
    },
  }),
)

const StyledField = styled(Field)(
  ({theme}) => ({
    '& label.Mui-focused': {
      color: 'gray',
    },
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.secondary.main} !important`,
    },
    '& .MuiFormHelperText-root': {
      fontSize: '1.4rem',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.main.bgLight,
    },

  }),
)

const StyledButton = styled(Button)(
  ({theme}) => ({
    '&.Mui-disabled': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.secondary.light,
    }
  }),
)


const KYCAnonymous = () => {
  const dispatch = useDispatch();
  const [stage, setStage] = useState(0);

  const getValidationSchema = (accountType) => {
    const baseValidation = {
      account_type: Yup.string().required('Account type is required'),
      recaptcha: Yup.string().required("ReCaptcha is required")
    };

    // Individual account - only validate individual fields
    if (accountType === 'individual') {
      return Yup.object().shape({
        ...baseValidation,
        full_name: Yup.string().trim().required('Full Name is required'),
        email: Yup.string().email('Please enter a valid email address').required('Email is required'),
        // Explicitly allow other fields to be empty/undefined
        company_legal_name: Yup.string().notRequired(),
        corporate_type: Yup.string().notRequired(),
        signatory_name: Yup.string().notRequired(),
        signatory_email: Yup.string().notRequired(),
        joint_applicant_1_name: Yup.string().notRequired(),
        joint_applicant_1_email: Yup.string().notRequired(),
        joint_applicant_2_name: Yup.string().notRequired(),
        joint_applicant_2_email: Yup.string().notRequired(),
        additional_signatories: Yup.array().notRequired()
      });
    }

    // Joint account - only validate joint applicant fields
    if (accountType === 'joint') {
      return Yup.object().shape({
        ...baseValidation,
        joint_applicant_1_name: Yup.string().trim().required('First applicant name is required'),
        joint_applicant_1_email: Yup.string().email('Please enter a valid email address').required('First applicant email is required'),
        joint_applicant_2_name: Yup.string().trim().required('Second applicant name is required'),
        joint_applicant_2_email: Yup.string().email('Please enter a valid email address').required('Second applicant email is required'),
        // Explicitly allow other fields to be empty/undefined
        full_name: Yup.string().notRequired(),
        email: Yup.string().notRequired(),
        company_legal_name: Yup.string().notRequired(),
        corporate_type: Yup.string().notRequired(),
        signatory_name: Yup.string().notRequired(),
        signatory_email: Yup.string().notRequired(),
        additional_signatories: Yup.array().notRequired()
      });
    }

    // Corporate account - only validate corporate fields
    if (accountType === 'corporate') {
      return Yup.object().shape({
        ...baseValidation,
        company_legal_name: Yup.string().trim().required('Company legal name is required'),
        corporate_type: Yup.string().required('Corporate type is required'),
        signatory_name: Yup.string().trim().required('Signatory name is required'),
        signatory_email: Yup.string().email('Please enter a valid email address').required('Signatory email is required'),
        additional_signatories: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().trim().required('Signatory name is required'),
            email: Yup.string().email('Please enter a valid email address').required('Signatory email is required'),
            sequence: Yup.number().required('Sequence order is required')
          })
        ).test('signatories-required', 'All signatory fields must be completed', function(value) {
          if (!value || value.length === 0) return true; // Empty array is allowed
          return value.every(signatory => 
            signatory.name && signatory.name.trim() && 
            signatory.email && signatory.email.trim()
          );
        }),
        // Explicitly allow other fields to be empty/undefined
        full_name: Yup.string().notRequired(),
        email: Yup.string().notRequired(),
        joint_applicant_1_name: Yup.string().notRequired(),
        joint_applicant_1_email: Yup.string().notRequired(),
        joint_applicant_2_name: Yup.string().notRequired(),
        joint_applicant_2_email: Yup.string().notRequired()
      });
    }

    // Default fallback - only require account_type and recaptcha
    return Yup.object().shape(baseValidation);
  };

  const initialValues = {
    account_type: 'individual',
    full_name: '',
    email: '',
    // Joint account fields
    joint_applicant_1_name: '',
    joint_applicant_1_email: '',
    joint_applicant_2_name: '',
    joint_applicant_2_email: '',
    // Corporate fields
    company_legal_name: '',
    corporate_type: '',
    signatory_name: '',
    signatory_email: '',
    additional_signatories: [],
    primary_signatory_sequence: 1,
    recaptcha: ''
  };

  const handleSubmit = async (values) => {
    delete values['recaptcha']
    await dispatch(submitAnonymousKYC(values)).unwrap();
    enqueueSnackbar("KYC verification request has been submitted", { variant: 'success' })
    setStage(2)
  }

  return (
    <ProfileBoxWrapper>
      <ProfileBox>
        <Box mb={3} textAlign="center">
          <img src={Logo} alt="Portfolio Platform" width={200} />
          <Typography variant="h3" color={"white"}>Due diligence and KYC Verification</Typography>
        </Box>

          <Box hidden={ stage === 2} sx={{mb: 3}}>
            Please note that your request will be forwarded to our Compliance department. They will process our regulatory Due diligence and KYC verification.<br/>
            This process is essential to verify your identity and maintain the integrity of our services. Subsequently, you will receive an email from our Back Office.<br/>
            This email will contain detailed instructions on how to proceed with the verification process.<br/>
            <a href="#" onClick={() => setStage(0)} hidden={stage === 0}>Read More</a>
          </Box>

        <Collapse in={stage === 0}>
          <ol>
            <li>Follow the instructions in the email to complete your application. This may include submitting personal information and relevant documents as required by our Compliance department.</li>
            <li>Once you submit your information, our Compliance will review your submission. The review process usually takes 24 to 48 hours upon receipt of your documents, after which your account status will be updated.</li>
            <li>Upon successful verification, you will be notified, and your account will be fully activated.</li>
          </ol>
          <StyledButton sx={{mt: 2}} type="submit" variant="contained" color="secondary" fullWidth  onClick={() => setStage(1)}> Continue </StyledButton>
        </Collapse>

        <Collapse in={stage === 1}>
          <Formik
            sx={{ mt: 3 }}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validate={(values) => {
              try {
                getValidationSchema(values.account_type).validateSync(values, { abortEarly: false });
                return {};
              } catch (err) {
                const errors = {};
                err.inner.forEach(error => {
                  errors[error.path] = error.message;
                });
                return errors;
              }
            }}
          >
            {({ handleSubmit, isSubmitting, errors, touched, setFieldValue, values, resetForm }) => {
              
              // Clear fields when account type changes
              const handleAccountTypeChange = (event) => {
                const newAccountType = event.target.value;
                setFieldValue('account_type', newAccountType);
                
                // Reset all other fields
                const clearedValues = {
                  account_type: newAccountType,
                  recaptcha: values.recaptcha, // Keep recaptcha
                  // Clear all other fields
                  full_name: '',
                  email: '',
                  joint_applicant_1_name: '',
                  joint_applicant_1_email: '',
                  joint_applicant_2_name: '',
                  joint_applicant_2_email: '',
                  company_legal_name: '',
                  corporate_type: '',
                  signatory_name: '',
                  signatory_email: '',
                  additional_signatories: []
                };
                
                // Reset form with cleared values
                resetForm({ values: clearedValues });
              };

              return (
              <Form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Account Type Selection */}
                  <Grid item xs={12}>
                    <StyledFormControl fullWidth>
                      <StyledField 
                        component={SelectInput} 
                        id="account_type" 
                        name="account_type" 
                        label="Type of Account"
                        onChange={handleAccountTypeChange}
                      >
                        <MenuItem value="individual">Individual</MenuItem>
                        <MenuItem value="corporate">Corporate</MenuItem>
                        <MenuItem value="joint">Joint</MenuItem>
                      </StyledField>
                    </StyledFormControl>
                  </Grid>

                  {/* Individual Fields */}
                  {values.account_type === 'individual' && (
                    <>
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="full_name" label="Full Name (as stated on Passport or National ID)" fullWidth />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="email" label="Email Address" fullWidth />
                      </Grid>
                    </>
                  )}

                  {/* Joint Account Fields */}
                  {values.account_type === 'joint' && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{color: 'white', mb: 1}}>First Applicant</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="joint_applicant_1_name" label="Full Name (as stated on Passport or National ID)" fullWidth />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="joint_applicant_1_email" label="Email Address" fullWidth />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{color: 'white', mb: 1, mt: 2}}>Second Applicant</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="joint_applicant_2_name" label="Full Name (as stated on Passport or National ID)" fullWidth />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="joint_applicant_2_email" label="Email Address" fullWidth />
                      </Grid>
                    </>
                  )}

                  {/* Corporate Fields */}
                  {values.account_type === 'corporate' && (
                    <>
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="company_legal_name" label="Company Legal Name" fullWidth />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <StyledFormControl fullWidth>
                          <StyledField component={SelectInput} id="corporate_type" name="corporate_type" label="Corporate Type">
                            <MenuItem value="Ltd">Ltd</MenuItem>
                            <MenuItem value="LLP">LLP</MenuItem>
                            <MenuItem value="SSAS">SSAS</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                          </StyledField>
                        </StyledFormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{color: 'white', mb: 1}}>Primary Signatory</Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="signatory_name" label="Signatory Full Name (as on Passport/ID)" fullWidth />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <StyledField component={StyledTextField} name="signatory_email" label="Signatory Email" fullWidth />
                      </Grid>

                      {/* Additional Signatories */}
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{color: 'white', mb: 1}}>Additional Signatories</Typography>
                        <FieldArray name="additional_signatories">
                          {({ push, remove }) => (
                            <Box>
                              {values.additional_signatories.map((signatory, index) => (
                                <Box key={index} sx={{border: 1, borderColor: 'grey.600', p: 2, mb: 2, borderRadius: 1, bgcolor: 'grey.900'}}>
                                  <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={5}>
                                      <StyledField 
                                        component={StyledTextField} 
                                        name={`additional_signatories.${index}.name`} 
                                        label="Full Name" 
                                        fullWidth 
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                      <StyledField 
                                        component={StyledTextField} 
                                        name={`additional_signatories.${index}.email`} 
                                        label="Email" 
                                        fullWidth 
                                        size="small"
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                      <Button 
                                        onClick={() => remove(index)} 
                                        variant="contained"
                                        color="error"
                                        sx={{
                                          minWidth: '40px',
                                          width: '40px',
                                          height: '40px',
                                          p: 0
                                        }}
                                      >
                                        <RemoveIcon />
                                      </Button>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))}
                              <StyledButton 
                                startIcon={<AddIcon />} 
                                onClick={() => push({ name: '', email: '', sequence: values.additional_signatories.length + 2 })}
                                variant="contained"
                                color="secondary"
                                size="small"
                                sx={{mb: 2}}
                              >
                                Add Signatory
                              </StyledButton>
                            </Box>
                          )}
                        </FieldArray>
                        {errors.additional_signatories && touched.additional_signatories && typeof errors.additional_signatories === 'string' ? (
                          <Typography component="p" fontSize="small" color="error.dark" sx={{ pl: 1.5 }}>{errors.additional_signatories}</Typography>
                        ) : null}
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} onChange={(value) => setFieldValue('recaptcha', value)}/>
                    {errors.recaptcha && touched.recaptcha ? (
                      <Typography component="p" fontSize="small" color="error.dark" sx={{ pl: 1.5 }}>{errors.recaptcha}</Typography>
                    ) : null}
                  </Grid>

                  <Grid item xs={12} >
                    <StyledButton sx={{mt: 2}} type="submit" variant="contained" color="secondary" fullWidth endIcon={isSubmitting? <CircularProgress size={20} color="secondary" /> :  <UploadIcon/>} disabled={isSubmitting}> REQUEST KYC VERIFICATION </StyledButton>
                  </Grid>
                </Grid>

              </Form>
              );
            }}
          </Formik>
        </Collapse>

        <Collapse in={stage === 2}>
           <Box mb={3} textAlign="center">
              <Typography variant="h4" color={"white"}>Thank you for request.</Typography>
              Please keep an eye out for a follow up email from us shortly.
           </Box>

        </Collapse>
      </ProfileBox>
    </ProfileBoxWrapper>
  );
};

export default KYCAnonymous;
